import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import {
  getTicketStatusInfo,
  getTicketTitleAndBody,
  getCategoryName,
  formatTicketDate,
} from '@/utils/residents/maintenance';
import { buildStatusTimeline } from '@/utils/residents/maintenance/statusTimeline';
import { HorizontalStatusTimeline } from '@/components/residents/maintenance/shared';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useHover } from '@/hooks/common/useHover';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { WEB_STYLES } from '@/utils/webStyles';
import { PressableScale } from '@/components/shared/redesign/PressableScale';
import { FadeInImage } from '@/components/shared/redesign/FadeInImage';
import { GlassCard } from '../GlassCard';
import { buildCardHoverStyle } from './buildCardHoverStyle';

interface MaintenanceHeroCardProps {
  ticket: IMaintenanceListResponse;
  onPress: () => void;
}

/**
 * Active-first list hero: the most recently touched open request, with status
 * label, title, photo thumbnail, meta line, and the horizontal mini-timeline.
 */
export const MaintenanceHeroCard: React.FC<MaintenanceHeroCardProps> = React.memo(({
  ticket,
  onPress,
}) => {
  const { colors } = useThemeColors();
  const { isHovered, hoverProps } = useHover();
  const reduceMotion = useReducedMotion();

  const info = getTicketStatusInfo(ticket);
  const isCompleted = info.status === 'completed';
  const statusColor = isCompleted ? colors.accent.green : info.color;
  const { title } = getTicketTitleAndBody(ticket);
  const category = getCategoryName(ticket);
  const displayDate = formatTicketDate(ticket.updatedOn || ticket.createdOn, 'medium', '');
  const submitted = formatTicketDate(ticket.createdOn, 'medium', '');
  const thumbUrl = ticket.files?.[0]?.fileUrl;
  const nodes = buildStatusTimeline(ticket);

  const hoverLift = buildCardHoverStyle({
    isHovered,
    reduceMotion,
    isWeb: Platform.OS === 'web',
    shadowHover: colors.card.shadowHover,
    liftPx: 2,
  });
  const hoverBorder = isHovered ? `${statusColor}99` : `${statusColor}55`;

  return (
    <PressableScale
      onPress={onPress}
      scaleTo={0.98}
      {...hoverProps}
      style={({ pressed }) => [
        styles.wrapper,
        webWrapperStyle,
        hoverLift,
        pressed ? { opacity: 0.85 } : null,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${title}, ${info.label}`}
      testID="maintenance-hero-card"
    >
      <GlassCard borderRadius={16} padding={20} style={{ borderColor: hoverBorder }}>
        <View style={styles.content}>
          <View style={styles.statusRow}>
            <View style={styles.statusLeft}>
              <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
              <Text style={[styles.statusLabel, { color: statusColor }]}>{info.label}</Text>
            </View>
            <Text style={[styles.dateText, { color: colors.text.dim }]}>{displayDate}</Text>
          </View>

          <Text style={[styles.title, { color: colors.text.primary }]} numberOfLines={2}>
            {title}
          </Text>

          <View style={styles.metaRow}>
            {/* Decorative — the title/status carry the information for AT. */}
            {thumbUrl ? <FadeInImage uri={thumbUrl} style={styles.thumb} /> : null}
            <View style={styles.metaText}>
              <Text style={[styles.metaLine, { color: colors.text.muted }]} numberOfLines={1}>
                {category} · WO-{ticket.maintenanceRequestId}
              </Text>
              {submitted ? (
                <Text style={[styles.metaLine, { color: colors.text.dim }]} numberOfLines={1}>
                  Submitted {submitted}
                </Text>
              ) : null}
            </View>
          </View>

          <HorizontalStatusTimeline nodes={nodes} />
        </View>
      </GlassCard>
    </PressableScale>
  );
});

MaintenanceHeroCard.displayName = 'MaintenanceHeroCard';

const webWrapperStyle =
  Platform.OS === 'web'
    ? ({ cursor: 'pointer', ...WEB_STYLES.transition('all', '0.15s', 'ease') } as any)
    : undefined;

const styles = StyleSheet.create({
  wrapper: { borderRadius: 16 },
  content: { gap: 12 },
  statusRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  statusLeft: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  statusDot: { width: 8, height: 8, borderRadius: 4 },
  statusLabel: { fontSize: 13, fontWeight: '600', lineHeight: 18 },
  dateText: { ...REDESIGN_TYPOGRAPHY.caption, lineHeight: 18 },
  title: { ...REDESIGN_TYPOGRAPHY.cardTitleLarge, fontWeight: '500', lineHeight: 24 },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  thumb: { width: 44, height: 44, borderRadius: 8 },
  metaText: { flex: 1, gap: 2 },
  metaLine: { ...REDESIGN_TYPOGRAPHY.caption, lineHeight: 17 },
});
